import React from 'react'
import '../App.css';
import "@limetech/lime-elements";
import "@limetech/lime-elements/dist/lime-elements/lime-elements.css";
import "@limetech/lime-elements/dist/lime-elements/style/color-palette-extended-light-mode-only.css";
import { LimelInput } from "../limel";
import { LimelBtn } from "../limel";
import { useSearchParams } from 'react-router-dom';
import { useRef } from 'react';
import { useEffect, useState } from 'react';
import { DisplayValidationResult } from "./DisplayValidationResult";

interface SendingDomainParams {
    domain: string;
    verificationCode: string;
    dkimSelector: string;
    mailServerIp: string;
    returnPathDomain: string;
}

interface SendingDomainClientValidationStatus {
    domain: boolean;
    verificationCode: boolean;
    dkimSelector: boolean;
    mailServerIp: boolean;
    returnPathDomain: boolean;
}

function Home() {

    const [searchParams, setSearchParams] = useSearchParams();

    //
    // Updating ref is synchronous (updating state is asynchronous), ref values are still persisted between render
    //
    const sendingDomainParams = useRef<SendingDomainParams>({
        domain: searchParams.get("domain") || "",
        verificationCode: searchParams.get("verificationcode") || "",
        dkimSelector: searchParams.get("dkimselector") || "",
        mailServerIp: searchParams.get("mailserverip") || "",
        returnPathDomain: searchParams.get("returnpath") || ""
    });

    //
    // If rp exist in query string, we should show return-path input fields
    //
    const validateReturnPath = searchParams.has('rp');

    //Domain was already verified
    const ownershipPreviouslyVerified = searchParams.has('ownershipverified');

    //
    // Client validation status
    //
    const [validationState, setValidationState] = useState<SendingDomainClientValidationStatus>({
        domain: true,
        verificationCode: true,
        dkimSelector: true,
        mailServerIp: true,
        returnPathDomain: true
    });

    //
    // Validation result
    //
    const [loading, setLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [validationResultDomain, setValidationResultDomain] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const scollToValidationResultRef = useRef<HTMLDivElement>(null);

    // @ts-expect-error: what event type for getting detail value?
    function handleChangedDomain(e) {
        sendingDomainParams.current.domain = e.detail;
        setValidationState({ ...validationState, [e.target.id]: true });
    }

    // @ts-expect-error: what event type for getting detail value?
    function handleChangedVerificationcode(e) {
        sendingDomainParams.current.verificationCode = e.detail;
        setValidationState({ ...validationState, [e.target.id]: true });
    }

    // @ts-expect-error: what event type for getting detail value?
    function handleChangedDkimselector(e) {
        sendingDomainParams.current.dkimSelector = e.detail;
        setValidationState({ ...validationState, [e.target.id]: true });
    }

    // @ts-expect-error: what event type for getting detail value?
    function handleChangedMailserverip(e) {
        sendingDomainParams.current.mailServerIp = e.detail;
        setValidationState({ ...validationState, [e.target.id]: true });
    }

    // @ts-expect-error: what event type for getting detail value?
    function handleChangedReturnPathDomain(e) {
        sendingDomainParams.current.returnPathDomain = e.detail;
        setValidationState({ ...validationState, [e.target.id]: true });
    }

    //
    // TODO - get domain regex from server
    //
    const domainRegex = /^((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9-_]+\.)*([a-zA-Z0-9-]+)\.[a-zA-Z0-9]{2,15}))/;

    function setAndGetValidationStatus() {
        let validationState: SendingDomainClientValidationStatus = {
            domain: domainRegex.test(sendingDomainParams.current.domain),
            verificationCode: !!sendingDomainParams.current.verificationCode,
            dkimSelector: !!sendingDomainParams.current.dkimSelector,
            mailServerIp: !!sendingDomainParams.current.mailServerIp,
            returnPathDomain: domainRegex.test(sendingDomainParams.current.returnPathDomain)
        };
        setValidationState(validationState);
        return validationState;
    }

    function formIsValid(validationStatus: SendingDomainClientValidationStatus) {
        return validationStatus.domain && validationStatus.verificationCode && validationStatus.dkimSelector &&
            (!validateReturnPath || (validationStatus.mailServerIp && validationStatus.returnPathDomain));
    }

    const handleSubmitForm = async () => {

        let validationState = setAndGetValidationStatus();
        let succeeded = false;

        if (formIsValid(validationState)) {

            let search = new URLSearchParams({
                domain: sendingDomainParams.current.domain,
                verificationcode: sendingDomainParams.current.verificationCode,
                dkimselector: sendingDomainParams.current.dkimSelector
            })

            if (ownershipPreviouslyVerified) {
                search.append("ownershipverified", "true");
            }

            if (validateReturnPath) {
                search.append("returnpath", sendingDomainParams.current.returnPathDomain);
                search.append("mailserverip", sendingDomainParams.current.mailServerIp);
                search.append("rp", "");
            }

            try {
                let res = await fetch('emaildeliverability?' + search, {
                    method: "GET",
                });

                setLoading(false);

                if (res.status === 200) {
                    succeeded = true;
                    let resJson = await res.json();
                    setSearchParams(search);
                    setValidationResult(resJson);
                    setValidationResultDomain(sendingDomainParams.current.domain);
                    if (scollToValidationResultRef.current !== null) {
                        scollToValidationResultRef.current.scrollIntoView();
                    }
                } else {
                    setErrorMessage(res.statusText);
                }

            } catch (err) {
                console.log(err);
            }
        }

        setLoading(false);
        if (!succeeded) {
            setValidationResult(null);
        }
    };

    useEffect(() => {
        if (searchParams.size > 0) {
            //
            // If query string parameters exist on page load, submit form
            //
            setLoading(true);
            handleSubmitForm();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onClick() {
        setErrorMessage("");
        setLoading(true);
        handleSubmitForm();
    }

    return (
        <div className="split-page-container">
            <div className="split-page left">
                <div className="app-form-container">
                    <h1>Enter a domain to validate</h1>
                    <div className="app-form">
                        <div className="input-element">
                            <LimelInput id="domain" label={"Domain"}
                                value={sendingDomainParams.current.domain}
                                onChange={handleChangedDomain}
                                required={true}
                                helperText="Please enter a valid domain"
                                invalid={!validationState.domain}
                            />
                        </div>
                        <div className="input-element">
                            <LimelInput id="verificationCode" label={"Verification code"}
                                value={sendingDomainParams.current.verificationCode}
                                onChange={handleChangedVerificationcode}
                                required={true}
                                helperText="Please enter lime verification code"
                                invalid={!validationState.verificationCode}
                            />
                        </div>
                        <div className="input-element">
                            <LimelInput id="dkimSelector" label={"DKIM selector"}
                                value={sendingDomainParams.current.dkimSelector}
                                onChange={handleChangedDkimselector}
                                required={true}
                                helperText="Please enter DKIM selector"
                                invalid={!validationState.dkimSelector}
                            />
                        </div>
                        {validateReturnPath &&
                            <div>
                                <div className="input-element">
                                    <LimelInput id="returnPathDomain" label={"Custom return path domain"}
                                        value={sendingDomainParams.current.returnPathDomain}
                                        onChange={handleChangedReturnPathDomain}
                                        required={true}
                                        helperText="Please enter a valid domain"
                                        invalid={!validationState.returnPathDomain}
                                    />
                                </div>
                                <div className="input-element">
                                    <LimelInput id="mailServerIp" label={"Mail server ip"}
                                        value={sendingDomainParams.current.mailServerIp}
                                        onChange={handleChangedMailserverip}
                                        required={true}
                                        helperText="Please enter mail server ip"
                                        invalid={!validationState.mailServerIp}
                                    />
                                </div>
                            </div>
                        }
                        {errorMessage &&
                            <span className="error-text">{errorMessage}</span>
                        }
                        <div className="app-form-buttons">
                            <LimelBtn label="Validate" primary={true} onClick={onClick} loading={loading}></LimelBtn>
                        </div>
                    </div>
                </div>
            </div>
            <div className="split-page rigth" ref={scollToValidationResultRef}>
                <DisplayValidationResult validationResult={validationResult} domain={validationResultDomain} showReturnPathResult={validateReturnPath}></DisplayValidationResult>
            </div>
        </div>
    );
}

export { Home };
